import React from 'react';
import { Layout } from 'antd';
import get from 'lodash/get';
import cs from 'classnames';
import moment from 'moment-timezone';
import { useSelector, useDispatch } from 'react-redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import AlertPopup from 'components/AlertPopup';
import AlertPopupHandler from 'components/AlertPopup/AlertPopupHandler';
import { permissions, canAccess } from 'helpers/permission';
import { apiUpdateUserSignature } from 'api/auth';
import { syncUpdateProfile } from 'store/actions/auth';
import Notifications from 'components/Notification';
import NotificationHandler from 'components/Notification/NotificationHandler';
import SignatureModal from 'components/SignatureModal';
import asyncComponent from 'util/asyncComponent';
import Sidebar from '../Sidebar/index';
import Topbar from '../Topbar/index';
import ViewAnnouncementsModal from '../ViewAnnouncementsModal';
import classes from './MainApp.module.scss';
import { TAB_SIZE } from '../../constants/ThemeSetting';
import ViewRequestReportModal from 'views/pages/IncidentReportsView/components/ViewRequestReportModal';

const { Content, Footer } = Layout;

const AsyncDashboard = asyncComponent(() => import('views/pages/Dashboard'));
const AsyncLicensesCertificates = asyncComponent(() =>
  import('views/pages/LicensesCertificates')
);
const AsyncRollSheets = asyncComponent(() => import('views/pages/RollSheets'));
const AsyncParticipantViewPIP = asyncComponent(() =>
  import('views/pages/ParticipantViewPIP')
);
const AsyncPIPRunTrial = asyncComponent(() =>
  import('views/pages/PIPRunTrial')
);
const AsyncTrialsApprovals = asyncComponent(() =>
  import('views/pages/TrialsApprovals')
);
const AsyncRollSheetReview = asyncComponent(() =>
  import('views/pages/RollSheetReview')
);
const AsyncRollSheetReviewDetails = asyncComponent(() =>
  import('views/pages/RollSheetReviewDetails')
);
const AsyncParticipantTrials = asyncComponent(() =>
  import('views/pages/ParticipantTrials')
);
const AsyncParticipantNewPIP = asyncComponent(() =>
  import('views/pages/ParticipantNewPIP')
);
const AsyncParticipantProfile = asyncComponent(() =>
  import('views/pages/ParticipantProfile')
);
const AsyncParticipants = asyncComponent(() =>
  import('views/pages/Participants')
);
const AsyncEmployeeApprovals = asyncComponent(() =>
  import('views/pages/EmployeeApprovals')
);
const AsyncRoster = asyncComponent(() => import('views/pages/Roster'));
const AsyncPrograms = asyncComponent(() => import('views/pages/Programs'));
const AsyncFacilities = asyncComponent(() => import('views/pages/Facilities'));
const AsyncUserProfile = asyncComponent(() =>
  import('views/pages/UserProfile')
);
const AsyncEmployees = asyncComponent(() => import('views/pages/Employees'));
const AsyncAnnouncements = asyncComponent(() =>
  import('views/pages/Announcements')
);
const AsyncActivityFeed = asyncComponent(() =>
  import('views/pages/ActivityFeed')
);
const AsyncBonusesReimbursements = asyncComponent(() =>
  import('views/pages/BonusesReimbursements')
);
const AsyncPayroll = asyncComponent(() => import('views/pages/Payroll'));
const AsyncIncidentReportsView = asyncComponent(() =>
  import('views/pages/IncidentReportsView')
);
const AsyncIncidentReportsNew = asyncComponent(() =>
  import('views/pages/IncidentReportsNew')
);
const AsyncIncidentReports = asyncComponent(() =>
  import('views/pages/IncidentReports')
);
const AsyncOfficeCalendar = asyncComponent(() =>
  import('views/pages/OfficeCalendar')
);
const AsyncDDASchedules = asyncComponent(() =>
  import('views/pages/DDASchedules')
);
const AsyncSchedules = asyncComponent(() => import('views/pages/Schedules'));
const AsyncReports = asyncComponent(() => import('views/pages/Reports'));
const AsyncServices = asyncComponent(() => import('views/pages/Services'));
const AsyncLocation = asyncComponent(() => import('views/pages/Location'));
const AsyncQuickbooksIntegration = asyncComponent(() =>
  import('views/pages/QuickbooksIntegration')
);
const AsyncAdmin = asyncComponent(() => import('views/pages/Admin'));
const AsyncApprovals = asyncComponent(() => import('views/pages/Approvals'));
const AsyncPendingAuth = asyncComponent(() =>
  import('views/pages/Approvals/PendingAuth')
);

const MainApp = ({ location }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(({ auth }) => auth.user);
  const userTimeZone = currentUser.timezone;
  moment.tz.setDefault(userTimeZone);
  const width = useSelector(({ preferences }) => get(preferences, 'width'));
  const isStopButton = useSelector(({ timer }) =>
    get(timer, 'isStarting', false)
  );
  const { pathname, search } = location || {};
  const isParticipantScheduleUrl =
    (pathname || '').includes('/participants/') &&
    (search || '').includes('?tab=schedule');
  const isPayrollUrl = (pathname || '').includes('/payroll');
  const isEmployeeApprovalsUrl = (pathname || '').includes(
    '/employee-approvals'
  );
  const isWhiteBg = (pathname || '').includes('/dda-schedules');
  const isOfficeCalendarPage = pathname === '/office-calendar';
  const isSchedulePage = pathname === '/schedules';
  const isDashboardPage = pathname === '/dashboard';
  const isParticipantsPage = pathname === '/participants';
  const isParticipantTrials = pathname === '/participant-trials';
  const isReviewRollSheetDetails =
    pathname.includes('/review-roll-sheets/') &&
    Number(pathname.split('/')?.[2] || 0) > 0;
  const isPIPRunTrial = pathname.endsWith('/run-trials');
  const isNewIncidentReportPage =
    pathname.startsWith('/create-incident-reports') ||
    pathname.startsWith('/create-na-incident-reports') ||
    pathname.startsWith('/edit-incident-reports/') ||
    pathname.startsWith('/incident-reports/');
  const hasTimerButton = canAccess(permissions.USE_TIMER);
  const isTimerRunningMobile = width < TAB_SIZE && isStopButton;
  const isSigned = !!currentUser.signature_font;
  const handleSignature = async (val, values, cb, errorCb) => {
    try {
      const tPayload = {
        signature_font: String(val),
        initial: values.short_name,
      };
      const res = await apiUpdateUserSignature(tPayload);
      if (res?.status) {
        NotificationHandler.showSuccess({
          description:
            res?.message ||
            'Your signature and initials were successfully saved.',
        });
        if (cb) cb();
        dispatch(syncUpdateProfile(tPayload));
      } else {
        if (cb) cb();
        if (errorCb) errorCb();
        NotificationHandler.showError({
          description: res?.message || 'Something went wrong!',
        });
      }
    } catch (error) {
      NotificationHandler.showError({
        description: 'Something went wrong!',
      });
    }
  };

  return (
    <Layout
      className={cs(
        'gx-app-layout layout-app-version-001',
        classes.layoutWrapper,
        {
          [classes.isPIPRunTrial]: isPIPRunTrial,
          [classes.isFullScreen]:
            isParticipantTrials || isReviewRollSheetDetails,
        }
      )}
    >
      <ViewRequestReportModal />
      {!isSigned && <SignatureModal onSubmit={handleSignature} />}
      <AlertPopup ref={AlertPopupHandler.setRef} />
      <Notifications ref={NotificationHandler.setRef} />
      <Sidebar />
      <Layout
        className={cs(classes.mainLayout, {
          [classes.whiteBg]: isWhiteBg,
          'layout-has-timer-button':
            hasTimerButton && width < TAB_SIZE && !isTimerRunningMobile,
          // 'layout-has-timer-button-running':
          //   hasTimerButton && isTimerRunningMobile,
        })}
        id="layout-inner"
      >
        <Topbar />
        <Content
          id="main-wrapper"
          className={cs(
            `gx-layout-content gx-container-wrap ${classes.layoutContent}`
          )}
        >
          <div
            className={cs(
              'gx-main-content-wrapper gx-mt-5 gx-mx-5 gx-p-0',
              classes.mainApp,
              {
                [classes.mainAppFlex]:
                  isParticipantScheduleUrl || isEmployeeApprovalsUrl,
                [classes.mainAppRelative]: isPayrollUrl,
                [classes.isDashboardPage]: isDashboardPage,
                [classes.isNewIncidentReportPage]: isNewIncidentReportPage,
                'main-inner-wrapper-participants': isParticipantsPage,
              },
              'main-inner-wrapper'
            )}
          >
            <Switch>
              <Route exact path={`/dashboard`} component={AsyncDashboard} />
              <Route
                exact
                path={`/certificates`}
                component={AsyncLicensesCertificates}
              />
              <Route exact path={`/roll-sheet`} component={AsyncRollSheets} />
              <Route
                exact
                path={`/participants/:id/pip/:pipId`}
                component={AsyncParticipantViewPIP}
              />
              <Route exact path={`/run-trials`} component={AsyncPIPRunTrial} />
              <Route
                exact
                path={`/my-trials-approvals`}
                component={AsyncTrialsApprovals}
              />
              <Route
                exact
                path={`/all-trials-approvals`}
                component={AsyncTrialsApprovals}
              />
              <Route
                exact
                path={`/review-roll-sheets`}
                component={AsyncRollSheetReview}
              />
              <Route
                exact
                path={`/review-roll-sheets/:id`}
                component={AsyncRollSheetReviewDetails}
              />
              <Route
                exact
                path={`/participant-trials`}
                component={AsyncParticipantTrials}
              />
              <Route
                exact
                path={`/participants/:id/edit-active-pip/:pipId`}
                component={AsyncParticipantNewPIP}
              />
              <Route
                exact
                path={`/participants/:id/edit-pip/:pipId`}
                component={AsyncParticipantNewPIP}
              />
              <Route
                exact
                path={`/participants/:id/create-pip`}
                component={AsyncParticipantNewPIP}
              />
              <Route
                exact
                path={`/participants/:id`}
                component={AsyncParticipantProfile}
              />
              <Route
                exact
                path={`/participants`}
                component={AsyncParticipants}
              />
              <Route
                exact
                path={`/employee-approvals`}
                component={AsyncEmployeeApprovals}
              />
              <Route exact path={`/roster`} component={AsyncRoster} />
              <Route exact path={`/programs`} component={AsyncPrograms} />
              <Route exact path={`/facilities`} component={AsyncFacilities} />
              <Route
                exact
                path={`/employees/:id`}
                component={AsyncUserProfile}
              />
              <Route exact path={`/employees`} component={AsyncEmployees} />
              <Route
                exact
                path={`/announcements`}
                component={AsyncAnnouncements}
              />
              <Route
                exact
                path={`/activity-feed`}
                component={AsyncActivityFeed}
              />
              <Route
                exact
                path={`/bonuses-reimbursements`}
                component={AsyncBonusesReimbursements}
              />
              <Route exact path={`/payroll`} component={AsyncPayroll} />
              <Route
                exact
                path={`/incident-reports/:id`}
                component={AsyncIncidentReportsView}
              />
              <Route
                exact
                path={`/incident-reports/:id/submit`}
                component={AsyncIncidentReportsView}
              />
              <Route
                exact
                path={`/incident-reports/:id/review`}
                component={AsyncIncidentReportsView}
              />
              <Route
                exact
                path={`/incident-reports/:id/qa`}
                component={AsyncIncidentReportsView}
              />
              <Route
                exact
                path={`/create-incident-reports`}
                component={AsyncIncidentReportsNew}
              />
              <Route
                exact
                path={`/create-na-incident-reports`}
                component={AsyncIncidentReportsNew}
              />
              <Route
                exact
                path={`/edit-incident-reports/:id`}
                component={AsyncIncidentReportsNew}
              />
              <Route
                exact
                path={`/incident-reports`}
                component={AsyncIncidentReports}
              />
              <Route
                exact
                path={`/office-calendar`}
                component={AsyncOfficeCalendar}
              />
              <Route
                exact
                path={`/dda-schedules`}
                component={AsyncDDASchedules}
              />
              <Route exact path={`/schedules`} component={AsyncSchedules} />
              <Route exact path={`/reports`} component={AsyncReports} />
              <Route exact path={`/services`} component={AsyncServices} />
              <Route exact path={`/locations`} component={AsyncLocation} />
              <Route
                exact
                path={`/quickbooks/callback`}
                component={AsyncQuickbooksIntegration}
              />
              <Route
                exact
                path={`/quickbooks-integration`}
                component={AsyncQuickbooksIntegration}
              />
              <Route exact path={`/admin`} component={AsyncAdmin} />
              <Route exact path={`/approvals`} component={AsyncApprovals} />
              <Route
                exact
                path={`/pending_auth`}
                component={AsyncPendingAuth}
              />
              <Redirect from="*" to="/dashboard" />
            </Switch>
          </div>
          {!isSchedulePage &&
            !isPayrollUrl &&
            !isOfficeCalendarPage &&
            !isParticipantTrials &&
            !isReviewRollSheetDetails && (
              <Footer
                className={cs({
                  [classes.addMargin]: window.navigator.userAgent.match(
                    /iPhone/i
                  ),
                })}
              >
                <div className="gx-layout-footer-content">
                  ©Copyright Life Inc {new Date().getFullYear()}
                </div>
              </Footer>
            )}
        </Content>
      </Layout>
      <ViewAnnouncementsModal forceHide={!isSigned} />
    </Layout>
  );
};
export default MainApp;
